import {
  isCancelledError,
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  listOrganizationMembers,
  addOrganizationMember,
  inviteDiscoveredUser,
  removeOrganizationMember,
  resendOrganizationInvite,
  updateOrganizationMemberRole,
  listMembersOnPersonalKeys,
} from '@kirin/api-client/api/members';
import { OrganizationRole } from '@kirin/api-client/contracts/organizations';
import { useCallback } from 'react';
import { apiClient } from '@/lib/api-client';
import { retryHandlers } from './helpers';
import { queryKeys } from './queryKeys';
import { cacheConfigForSharedRoster } from './queryConfig';

/**
 * Reload the member roster right after the admin's OWN membership change
 * (KIR-4887).
 *
 * The backend caches the Descope roster in Redis for every Cloud Run replica,
 * and the change clears that copy fleet-wide. A plain reload could still join a
 * roster fetch another replica started before the change and show the
 * pre-change roster (the change looks reverted), so this reload asks for
 * `fresh`, which the backend reads from Descope on whichever replica serves
 * it. The personal-key list (owners only) is reloaded the same way when it is
 * loaded. A failed fresh read falls back to a normal invalidation.
 *
 * `fetchQuery` joins a fetch already running for the key, which may have
 * started before this change (a focus refetch, or the reload after an earlier
 * change). So any in-flight fetch is cancelled first, and a reload cancelled
 * by a later one is left to that later one.
 *
 * Not awaited by callers: the mutation settles without waiting for the reload,
 * as it did with `invalidateQueries`.
 */
export async function refetchMemberRosterFresh(
  queryClient: QueryClient,
  organizationId: string
): Promise<void> {
  const membersKey = queryKeys.organizations.members(organizationId);
  const personalKeysKey =
    queryKeys.organizations.personalKeyMembers(organizationId);

  const reloads: Array<{
    queryKey: readonly unknown[];
    run: () => Promise<unknown>;
  }> = [
    {
      queryKey: membersKey,
      run: async () => {
        await queryClient.cancelQueries({ queryKey: membersKey, exact: true });
        return queryClient.fetchQuery({
          queryKey: membersKey,
          queryFn: () =>
            listOrganizationMembers(apiClient, organizationId, { fresh: true }),
          staleTime: 0,
        });
      },
    },
  ];
  if (queryClient.getQueryData(personalKeysKey) !== undefined) {
    reloads.push({
      queryKey: personalKeysKey,
      run: async () => {
        await queryClient.cancelQueries({
          queryKey: personalKeysKey,
          exact: true,
        });
        return queryClient.fetchQuery({
          queryKey: personalKeysKey,
          queryFn: () =>
            listMembersOnPersonalKeys(apiClient, organizationId, {
              fresh: true,
            }),
          staleTime: 0,
        });
      },
    });
  }

  const results = await Promise.allSettled(reloads.map(r => r.run()));
  results.forEach((result, index) => {
    if (result.status === 'rejected' && !isCancelledError(result.reason)) {
      void queryClient.invalidateQueries({
        queryKey: reloads[index]?.queryKey,
      });
    }
  });
}

/**
 * The Users page Refresh control (KIR-4887): reload the roster with
 * {@link refetchMemberRosterFresh}, so an explicit Refresh shows changes made
 * outside Kirin (Descope console, SCIM) at once instead of the backend's
 * cached copy (up to ~5 min old).
 *
 * @param organizationId - The organization ID (no-op while undefined)
 * @returns A callback that resolves once the reload settles
 */
export function useRefreshMemberRoster(
  organizationId: string | undefined
): () => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    if (!organizationId) return;
    await refetchMemberRosterFresh(queryClient, organizationId);
  }, [queryClient, organizationId]);
}

/**
 * Hook to fetch organization members list
 *
 * Uses React Query for caching and automatic refetching.
 * Uses the shared-roster cache config (KIR-4887): refetches on window focus to
 * catch changes from other admins, but does not poll — the roster is backed by
 * Descope, which rate-limits user search per project. A 503 (rate-limited) is
 * retried at most once.
 *
 * @param organizationId - The organization ID
 * @param enabled - Whether to enable the query (default: true if organizationId provided)
 *
 * @example
 * const { data: members, isLoading, error } = useMembers(organizationId);
 *
 * @returns React Query result with members array
 */
export function useMembers(organizationId: string | undefined, enabled = true) {
  const isEnabled = enabled && !!organizationId;
  return useQuery({
    queryKey: queryKeys.organizations.members(organizationId ?? ''),
    queryFn: () => {
      if (!organizationId) throw new Error('organizationId is required');
      return listOrganizationMembers(apiClient, organizationId);
    },
    enabled: isEnabled,
    ...cacheConfigForSharedRoster,
    retry: retryHandlers.rateLimited,
  });
}

/**
 * Hook to fetch org members still using a personal API key (KIR-3786, AC2/AC3).
 *
 * Owner-only endpoint — gate `enabled` on the caller's owner role so
 * non-owners never fire a request that would 403.
 *
 * @param organizationId - The organization ID
 * @param enabled - Whether to run the query (default true when orgId provided)
 * @returns React Query result with the personal-key members array
 */
export function usePersonalKeyMembers(
  organizationId: string | undefined,
  enabled = true
) {
  const isEnabled = enabled && !!organizationId;
  return useQuery({
    queryKey: queryKeys.organizations.personalKeyMembers(organizationId ?? ''),
    queryFn: () => {
      if (!organizationId) throw new Error('organizationId is required');
      return listMembersOnPersonalKeys(apiClient, organizationId);
    },
    enabled: isEnabled,
    ...cacheConfigForSharedRoster,
    retry: retryHandlers.rateLimited,
  });
}

type AddMemberParams = {
  organizationId: string;
  email: string;
  role?: OrganizationRole;
};

/**
 * Hook to add a member to an organization
 *
 * Uses React Query mutation with automatic cache invalidation.
 *
 * @example
 * const { mutateAsync: addMember, isPending, error } = useAddMember();
 * await addMember({ organizationId, email, role: 'member' });
 *
 * @returns React Query mutation result
 */
export function useAddMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      email,
      role = OrganizationRole.Member,
    }: AddMemberParams) =>
      addOrganizationMember(apiClient, organizationId, {
        email,
        role,
      }),
    onSuccess: (_, { organizationId }) => {
      // Fresh reload so the new member shows whichever backend replica serves
      // it (KIR-4887). The organizations list is untouched: adding a member
      // doesn't change which organizations the current user belongs to.
      void refetchMemberRosterFresh(queryClient, organizationId);
    },
  });
}

/**
 * Hook to invite a Discovered user (KIR-3908), converting the lightweight row
 * into a full account while preserving its findings/alerts.
 *
 * @example
 * const { mutateAsync: invite, isPending } = useInviteDiscoveredUser();
 * await invite({ organizationId, userId });
 */
export function useInviteDiscoveredUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      userId,
    }: {
      organizationId: string;
      userId: string;
    }) => inviteDiscoveredUser(apiClient, organizationId, userId),
    onSuccess: (_, { organizationId }) => {
      void refetchMemberRosterFresh(queryClient, organizationId);
    },
  });
}

/**
 * Hook to remove a member from an organization
 *
 * Uses React Query mutation with automatic cache invalidation.
 *
 * @example
 * const { mutateAsync: removeMember, isPending, error } = useRemoveMember();
 * await removeMember({ organizationId, userId });
 *
 * @returns React Query mutation result
 */
export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      userId,
    }: {
      organizationId: string;
      userId: string;
    }) => removeOrganizationMember(apiClient, organizationId, userId),
    onSuccess: (_, { organizationId }) => {
      // Fresh reload so the removal shows whichever backend replica serves it
      // (KIR-4887).
      void refetchMemberRosterFresh(queryClient, organizationId);
    },
  });
}

/**
 * Hook to re-send a pending or expired invitation (KIR-4040).
 *
 * Mirrors {@link useRemoveMember}: rotates the invite token (invalidating the
 * old link) and re-sends the invitation email, then invalidates the members
 * list so the row refreshes (it stays "Invited"). Owner/manager only — the
 * route enforces the role independently.
 *
 * @example
 * const { mutateAsync: resendInvite, isPending, error } = useResendInvite();
 * await resendInvite({ organizationId, userId });
 *
 * @returns React Query mutation result
 */
export function useResendInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      userId,
    }: {
      organizationId: string;
      userId: string;
    }) => resendOrganizationInvite(apiClient, organizationId, userId),
    onSuccess: (_, { organizationId }) => {
      // Invalidate members list cache so the resent row refreshes
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.members(organizationId),
      });
    },
  });
}

/**
 * Hook to update a member's role in an organization
 *
 * Uses React Query mutation with automatic cache invalidation.
 *
 * @example
 * const { mutateAsync: updateRole, isPending, error } = useUpdateMemberRole();
 * await updateRole({ organizationId, userId, role: 'manager' });
 *
 * @returns React Query mutation result
 */
export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      organizationId,
      userId,
      role,
    }: {
      organizationId: string;
      userId: string;
      role: string;
    }) => updateOrganizationMemberRole(apiClient, organizationId, userId, role),
    onSuccess: (_, { organizationId }) => {
      // Fresh reload so the new role shows whichever backend replica serves it
      // (KIR-4887).
      void refetchMemberRosterFresh(queryClient, organizationId);
    },
  });
}
